import React, { useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';

interface ContractorPerformance {
  id: string;
  userId: string;
  company: string;
  rating: number;
  totalRatings: number;
  completionRate: number;
  totalProjects: number;
  totalCompleted: number;
  averageCompletionTime: number;
  yearsInBusiness: number;
  specialties: string[];
  user: {
    email: string;
  };
}

interface PerformanceMetrics {
  averageRating: number;
  averageCompletionRate: number;
  totalActiveContractors: number;
  topPerformers: ContractorPerformance[];
  underPerformers: ContractorPerformance[];
}

const PerformanceMonitoring: React.FC = () => {
  const [contractors, setContractors] = useState<ContractorPerformance[]>([]);
  const [metrics, setMetrics] = useState<PerformanceMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'top' | 'average' | 'under'>('all');
  const { get } = useApi();

  useEffect(() => {
    fetchPerformanceData();
  }, []);

  const fetchPerformanceData = async () => {
    try {
      setLoading(true);
      const [contractorsResponse, metricsResponse] = await Promise.all([
        get('/api/admin/contractors/performance-monitoring'),
        get('/api/admin/contractors/performance-metrics')
      ]);

      if (contractorsResponse.success) {
        setContractors(contractorsResponse.data);
      } else {
        setError(contractorsResponse.message || 'Failed to fetch contractors');
      }

      if (metricsResponse.success) {
        setMetrics(metricsResponse.data);
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const filteredContractors = contractors.filter(contractor => {
    switch (filter) {
      case 'top':
        return contractor.rating >= 4.5 && contractor.completionRate >= 90;
      case 'average':
        return contractor.rating >= 3.0 && contractor.rating < 4.5 && contractor.completionRate >= 70;
      case 'under':
        return contractor.rating < 3.0 || contractor.completionRate < 70;
      default:
        return true;
    }
  });

  const getPerformanceColor = (rating: number, completionRate: number) => {
    if (rating >= 4.5 && completionRate >= 90) return 'bg-green-100 text-green-800';
    if (rating >= 3.0 && completionRate >= 70) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getPerformanceText = (rating: number, completionRate: number) => {
    if (rating >= 4.5 && completionRate >= 90) return 'Top Performer';
    if (rating >= 3.0 && completionRate >= 70) return 'Average';
    return 'Under Performer';
  };

  const formatDuration = (days: number) => {
    if (days < 30) return `${days} days`;
    const months = Math.floor(days / 30);
    const remainingDays = days % 30;
    return `${months}mo ${remainingDays}d`;
  };

  if (loading) return <div className="p-6">Loading performance monitoring data...</div>;
  if (error) return <div className="p-6 text-red-500">Error: {error}</div>;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Performance Monitoring</h2>
        <p className="text-gray-600">Monitor contractor performance metrics and identify areas for improvement</p>
      </div>

      {/* Performance Metrics Overview */}
      {metrics && (
        <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="text-2xl font-bold text-blue-600">{metrics.averageRating.toFixed(1)}</div>
            <div className="text-sm text-gray-500">Average Rating</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="text-2xl font-bold text-green-600">{metrics.averageCompletionRate.toFixed(1)}%</div>
            <div className="text-sm text-gray-500">Average Completion Rate</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="text-2xl font-bold text-purple-600">{metrics.totalActiveContractors}</div>
            <div className="text-sm text-gray-500">Active Contractors</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="text-2xl font-bold text-orange-600">{metrics.topPerformers.length}</div>
            <div className="text-sm text-gray-500">Top Performers</div>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="mb-6">
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
          {[
            { key: 'all', label: 'All', count: contractors.length },
            { key: 'top', label: 'Top Performers', count: contractors.filter(c => c.rating >= 4.5 && c.completionRate >= 90).length },
            { key: 'average', label: 'Average', count: contractors.filter(c => c.rating >= 3.0 && c.rating < 4.5 && c.completionRate >= 70).length },
            { key: 'under', label: 'Under Performers', count: contractors.filter(c => c.rating < 3.0 || c.completionRate < 70).length }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key as any)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filter === tab.key
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>
      </div>

      {/* Contractors Performance Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contractor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Performance Metrics
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Project Stats
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredContractors.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center text-gray-500">
                    No contractors found for the selected filter.
                  </td>
                </tr>
              ) : (
                filteredContractors.map((contractor) => (
                  <tr key={contractor.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {contractor.company || 'Contractor'}
                        </div>
                        <div className="text-sm text-gray-500">{contractor.user.email}</div>
                        <div className="text-sm text-gray-500">
                          {contractor.yearsInBusiness} years experience
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        <div className="text-sm text-gray-900">
                          Rating: <span className="font-medium">{contractor.rating.toFixed(1)}</span>
                          <span className="text-gray-500"> ({contractor.totalRatings} reviews)</span>
                        </div>
                        <div className="text-sm text-gray-900">
                          Completion Rate: <span className="font-medium">{contractor.completionRate.toFixed(1)}%</span>
                        </div>
                        <div className="text-sm text-gray-900">
                          Avg Duration: <span className="font-medium">{formatDuration(contractor.averageCompletionTime)}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        <div className="text-sm text-gray-900">
                          Total Projects: <span className="font-medium">{contractor.totalProjects}</span>
                        </div>
                        <div className="text-sm text-gray-900">
                          Completed: <span className="font-medium">{contractor.totalCompleted}</span>
                        </div>
                        <div className="text-sm text-gray-500">
                          Specialties: {contractor.specialties.join(', ')}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPerformanceColor(contractor.rating, contractor.completionRate)}`}>
                        {getPerformanceText(contractor.rating, contractor.completionRate)}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Performance Insights */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Top Performers */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Performers</h3>
          <div className="space-y-3">
            {metrics?.topPerformers.slice(0, 5).map((contractor, index) => (
              <div key={contractor.id} className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {contractor.company || 'Contractor'}
                  </div>
                  <div className="text-xs text-gray-500">{contractor.user.email}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-green-600">
                    {contractor.rating.toFixed(1)} ⭐
                  </div>
                  <div className="text-xs text-gray-500">
                    {contractor.completionRate.toFixed(1)}% completion
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Under Performers */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Under Performers</h3>
          <div className="space-y-3">
            {metrics?.underPerformers.slice(0, 5).map((contractor, index) => (
              <div key={contractor.id} className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {contractor.company || 'Contractor'}
                  </div>
                  <div className="text-xs text-gray-500">{contractor.user.email}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-red-600">
                    {contractor.rating.toFixed(1)} ⭐
                  </div>
                  <div className="text-xs text-gray-500">
                    {contractor.completionRate.toFixed(1)}% completion
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Performance Guidelines */}
      <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-yellow-900 mb-3">Performance Guidelines</h3>
        <div className="text-sm text-yellow-800 space-y-2">
          <p>• <strong>Top Performers:</strong> Rating ≥ 4.5 stars and completion rate ≥ 90%</p>
          <p>• <strong>Average Performers:</strong> Rating 3.0-4.4 stars and completion rate ≥ 70%</p>
          <p>• <strong>Under Performers:</strong> Rating &lt; 3.0 stars or completion rate &lt; 70%</p>
          <p>• Contractors with consistently low performance may require additional support or review</p>
          <p>• Consider implementing performance improvement plans for under-performing contractors</p>
        </div>
      </div>
    </div>
  );
};

export default PerformanceMonitoring;

